import { useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MOCK_CREW, MOCK_DRILLS, MOCK_VESSELS, resolveClientKey, getClientVessels, getClientCrew, getClientDrills } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { GraduationCap, AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const trainingItems = [
  "STCW Basic Safety", "Fast Rescue Craft", "HUET/BOSIET", "Medical First Aid",
  "Fire Prevention", "Personal Survival", "Security Awareness", "Ship Handling",
];

const TrainingCompetency = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientCrew = clientKey ? getClientCrew(clientKey) : MOCK_CREW;
  const clientDrills = clientKey ? getClientDrills(clientKey) : MOCK_DRILLS;
  const gaps = clientCrew.filter(c => c.certStatus !== "valid");

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Personnel</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Training & Competency</h1>
      </div>

      <Tabs defaultValue="matrix" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="matrix">Training Matrix</TabsTrigger>
          <TabsTrigger value="drills">Drill Log</TabsTrigger>
          <TabsTrigger value="gaps">Competency Gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="matrix">
          <div className="portal-card overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                <th className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">Crew Member</th>
                <th className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">Vessel</th>
                {trainingItems.slice(0, 5).map(t => <th key={t} className="px-3 py-2.5 text-center font-medium text-[hsl(215,12%,50%)] text-[9px]">{t}</th>)}
              </tr></thead>
              <tbody>
                {MOCK_CREW.map(c => (
                  <tr key={c.id} className="border-b border-[hsl(220,15%,92%)]">
                    <td className="px-4 py-2.5 font-medium">{c.name}</td>
                    <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{c.vessel}</td>
                    {trainingItems.slice(0, 5).map((_, i) => {
                      const s = i === 0 && c.certStatus === "expiring" ? "expiring" : i === 0 && c.certStatus === "expired" ? "expired" : "valid";
                      return <td key={i} className="px-3 py-2.5 text-center"><span className={`inline-block w-3 h-3 rounded-full ${s === "valid" ? "bg-[#28A745]" : s === "expiring" ? "bg-[#FFC107]" : "bg-[#DC3545]"}`} /></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="drills">
          <div className="portal-card">
            <div className="p-4 flex items-center justify-between border-b border-[hsl(220,15%,90%)]">
              <h2 className="font-bold">Drill Log</h2>
              <Button onClick={() => toast.success("Created: Request New Drill", { description: "New entry added." })} variant="outline" size="sm">Request New Drill</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Date", "Vessel", "Drill Type", "Participants", "Outcome", "Notes"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {MOCK_DRILLS.map(d => (
                    <tr key={d.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-4 py-2.5">{d.date}</td>
                      <td className="px-4 py-2.5">{d.vessel}</td>
                      <td className="px-4 py-2.5 font-medium">{d.type}</td>
                      <td className="px-4 py-2.5 text-center">{d.participants}</td>
                      <td className="px-4 py-2.5"><span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">{d.outcome}</span></td>
                      <td className="px-4 py-2.5 text-[hsl(215,12%,50%)]">{d.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="gaps">
          {gaps.length === 0 ? (
            <div className="portal-card p-8 text-center"><p className="text-sm text-[#28A745] font-medium">✓ All crew certifications are current</p></div>
          ) : (
            <div className="space-y-2">
              {gaps.map(c => (
                <div key={c.id} className="portal-card p-4 border-l-4 border-[#DC3545] flex items-center gap-4">
                  <AlertTriangle className="w-5 h-5 text-[#DC3545]" />
                  <div>
                    <p className="text-sm font-bold">{c.name}</p>
                    <p className="text-xs text-[hsl(215,12%,50%)]">{c.vessel} · {c.role}</p>
                    <p className="text-xs text-[#DC3545] mt-1">
                      {c.certStatus === "expired" ? `STCW expired: ${c.stcwExpiry}` : `STCW expiring soon: ${c.stcwExpiry}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default TrainingCompetency;
